// lib/stores/notificationStore.js
import { defineStore } from 'pinia';

export const useNotificationStore = defineStore('notification', {
  state: () => ({
    notifications: [],
    loading: false,
    error: null,
    pagination: {
      page: 1,
      limit: 20,
      total: 0,
      hasMore: true,
    },
  }),

  getters: {
    unreadCount: (state) =>
      state.notifications.filter((n) => !n.is_read).length,

    unreadNotifications: (state) =>
      state.notifications.filter((n) => !n.is_read),

    readNotifications: (state) => state.notifications.filter((n) => n.is_read),

    notificationsByType: (state) => (type) =>
      state.notifications.filter((n) => n.type === type),
  },

  actions: {
    // Helper method to handle API calls to Supabase Edge Function
    async apiCall(operation, options = {}) {
      const config = useRuntimeConfig();
      const supabaseUrl = config.public.SUPABASE_URL;
      const supabaseAnonKey = config.public.SUPABASE_KEY;

      // Get token from localStorage (same as before)
      let token = localStorage.getItem('session');
      token = token ? JSON.parse(token).access_token : '';

      // Build URL with operation parameter
      const url = new URL(`${supabaseUrl}/functions/v1/notifications`);
      url.searchParams.set('operation', operation);

      // Add query parameters if provided
      if (options.params) {
        Object.entries(options.params).forEach(([key, value]) => {
          url.searchParams.set(key, value);
        });
      }

      const response = await fetch(url.toString(), {
        method: options.method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: supabaseAnonKey,
          ...(options.headers || {}),
        },
        ...(options.body && { body: JSON.stringify(options.body) }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'API request failed');
      }

      return response.json();
    },

    // Helper method to handle errors
    handleError(error, action) {
      console.error(`Error ${action}:`, error, error.message);
      this.error = error.message;
      throw error;
    },

    async fetchNotifications(options = {}) {
      const {
        page = 1,
        limit = 20,
        unreadOnly = false,
        append = false,
      } = options;

      this.loading = true;
      this.error = null;

      try {
        const params = {
          page: page.toString(),
          limit: limit.toString(),
        };

        if (unreadOnly) {
          params.is_read = 'false';
        }

        const data = await this.apiCall('fetch', { params });

        if (append) {
          this.notifications.push(...data.notifications);
        } else {
          this.notifications = data.notifications;
        }

        this.pagination = data.pagination;
      } catch (error) {
        this.handleError(error, 'fetching notifications');
      } finally {
        this.loading = false;
      }
    },

    async loadMoreNotifications() {
      if (!this.pagination.hasMore || this.loading) return;

      await this.fetchNotifications({
        page: this.pagination.page + 1,
        limit: this.pagination.limit,
        append: true,
      });
    },

    async markAsRead(notificationIds) {
      const ids = Array.isArray(notificationIds)
        ? notificationIds
        : [notificationIds];

      try {
        await this.apiCall('mark-read', {
          method: 'POST',
          body: { ids },
        });

        // Update local state
        const timestamp = new Date().toISOString();
        ids.forEach((id) => {
          const notification = this.notifications.find((n) => n.id === id);
          if (notification) {
            notification.is_read = true;
            notification.read_at = timestamp;
          }
        });
      } catch (error) {
        this.handleError(error, 'marking notifications as read');
      }
    },

    async markAllAsRead() {
      try {
        await this.apiCall('mark-all-read', {
          method: 'POST',
        });

        // Update local state
        const timestamp = new Date().toISOString();
        this.notifications.forEach((notification) => {
          if (!notification.is_read) {
            notification.is_read = true;
            notification.read_at = timestamp;
          }
        });
      } catch (error) {
        this.handleError(error, 'marking all notifications as read');
      }
    },

    async deleteNotifications(notificationIds) {
      const ids = Array.isArray(notificationIds)
        ? notificationIds
        : [notificationIds];

      try {
        await this.apiCall('delete', {
          method: 'DELETE',
          body: { ids },
        });

        // Remove from local state
        this.notifications = this.notifications.filter(
          (n) => !ids.includes(n.id)
        );
      } catch (error) {
        this.handleError(error, 'deleting notifications');
      }
    },

    async createNotification(notificationData) {
      try {
        const data = await this.apiCall('create', {
          method: 'POST',
          body: notificationData,
        });

        return data.notification;
      } catch (error) {
        this.handleError(error, 'creating notification');
      }
    },

    addNotification(notification) {
      // Add new notification to the beginning
      this.notifications.unshift(notification);

      // Keep only the latest notifications based on limit
      const maxNotifications = this.pagination.limit * 3; // Keep 3 pages worth
      if (this.notifications.length > maxNotifications) {
        this.notifications = this.notifications.slice(0, maxNotifications);
      }
    },

    clearNotifications() {
      this.notifications = [];
      this.error = null;
      this.pagination = {
        page: 1,
        limit: 20,
        total: 0,
        hasMore: true,
      };
    },

    async fetchUnreadCount() {
      try {
        const data = await this.apiCall('unread-count');
        return data.unread_count;
      } catch (error) {
        this.handleError(error, 'fetching unread count');
        return 0;
      }
    },
  },
});
